// Self-serve sign-up on the portal's own screens (native sign-in environments): work email, name, company and a
// password on one screen, then the code Entra emails to confirm the address. Done, they're signed in and the
// workspace is created for the company they gave (SessionProvider's first /workspace call carries it).
import { useState } from "react";
import type { SignUpCodeRequiredState } from "@azure/msal-browser/custom-auth";
import { advanceSignUp, isExpired, passwordHint, passwordRefused, SIGNUP_COMPANY_KEY, signInScopes, wrong, type NativeClient, type SignUpOutcome } from "../auth/native";
import { AuthActions, AuthBadge, AuthFrame, AuthHeading, authNote, authText, CodeInput, PasswordInput, TextButton, useCountdown } from "../shared/AuthFrame";
import { Button, Input } from "../ui";

type Problems = Partial<Record<"email" | "name" | "company" | "password" | "form", string>>;

export function NativeSignUp({ native, scope, siteUrl }: { native: NativeClient; scope: string; siteUrl: string }) {
  const [f, setF] = useState({ email: "", name: "", company: "", password: "" });
  const [problems, setProblems] = useState<Problems>({});
  const [existing, setExisting] = useState(false);
  const [codeState, setCodeState] = useState<SignUpCodeRequiredState | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const resend = useCountdown(30);

  const values = () => ({ password: f.password, displayName: f.name.trim(), company: f.company.trim() });
  const edit = (key: keyof typeof f, value: string) => {
    setF({ ...f, [key]: value });
    setProblems({ ...problems, [key]: undefined, form: undefined });
    if (key === "email") setExisting(false);
  };

  /** Signed up: sign in, keep the company for the workspace, and open the portal. */
  const finish = async (outcome: Extract<SignUpOutcome, { kind: "done" }>) => {
    sessionStorage.setItem(SIGNUP_COMPANY_KEY, f.company.trim());
    const signedIn = await outcome.state.signIn({ scopes: signInScopes(scope) });
    window.location.assign(signedIn.isCompleted() ? "/" : `/?email=${encodeURIComponent(f.email.trim())}`);
  };

  const follow = async (outcome: SignUpOutcome) => {
    if (outcome.kind === "done") return finish(outcome);
    if (outcome.kind === "code") {
      setCode("");
      resend.restart(outcome.state.getCodeResendInterval() || 30);
      return setCodeState(outcome.state);
    }
    const error = outcome.error;
    if (error?.isUserAlreadyExists?.()) { setExisting(true); return setProblems({ email: "You already have an account with this email." }); }
    if (error?.isInvalidUsername?.()) return setProblems({ email: "That doesn’t look like an email address." });
    if (error?.isInvalidPassword?.()) { setCodeState(null); return setProblems({ password: passwordRefused }); }
    if (error?.isInvalidCode?.()) return setProblems({ form: "That code didn’t work. It may have expired: send a new one." });
    if (isExpired(error)) { setCodeState(null); return setProblems({ form: "That took a while, so we started over. Create your account again." }); }
    setProblems({ form: wrong("sign-up", error) });
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    const found: Problems = {
      email: /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email.trim()) ? undefined : "Enter your work email.",
      name: f.name.trim() ? undefined : "Tell us your name.",
      company: f.company.trim().length >= 2 ? undefined : "Which company is this for?",
      password: f.password ? undefined : "Choose a password.",
    };
    if (Object.values(found).some(Boolean)) return setProblems(found);
    setBusy(true);
    try {
      const result = await native.signUp({ username: f.email.trim(), password: f.password, attributes: { displayName: f.name.trim() } });
      await follow(await advanceSignUp(result, values()));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (value: string) => {
    if (!codeState) return;
    setBusy(true);
    setProblems({});
    try {
      await follow(await advanceSignUp(await codeState.submitCode(value), values()));
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (!codeState) return;
    setProblems({});
    const result = await codeState.resendCode();
    if (result.isCodeRequired()) { setCode(""); resend.restart(); setCodeState(result.state); }
    else setProblems({ form: wrong("sign-up resend code", result.error) });
  };

  if (codeState) {
    const length = codeState.getCodeLength() || 8;
    return (
      <AuthFrame footer={<>Typo in your email? <TextButton onClick={() => setCodeState(null)} style={{ fontSize: 14 }}>Go back</TextButton></>}>
        <AuthBadge tone="info" icon="mail" />
        <AuthHeading title="Confirm your email" text={<>Enter the {length}-digit code we sent to <b style={{ color: "var(--text-strong)" }}>{f.email.trim()}</b>.</>} />
        <CodeInput length={length} value={code} invalid={!!problems.form} onChange={(value) => { setCode(value); setProblems({}); if (value.length === length) void submitCode(value); }} />
        {problems.form && <p role="alert" style={{ ...authText, fontSize: 14, color: "var(--status-mismatch-fg)", marginTop: -14 }}>{problems.form}</p>}
        <AuthActions column>
          <Button size="lg" fullWidth loading={busy} disabled={code.length < length} onClick={() => void submitCode(code)}>Verify email</Button>
          <p style={authNote}>{resend.left > 0 ? `Nothing yet? Resend in 0:${String(resend.left).padStart(2, "0")}` : <>Nothing yet? Check spam, or <TextButton onClick={() => void resendCode()}>resend the code</TextButton></>}</p>
        </AuthActions>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <AuthHeading title="Create your account" text="Free during the beta. No card, no sales call." />
      <form noValidate onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <Input size="lg" label="Work email" type="email" placeholder="you@company.com" autoComplete="email" autoFocus value={f.email} onChange={(e) => edit("email", e.target.value)}
            error={problems.email ? <>{problems.email}{existing && <> <a href={`/?email=${encodeURIComponent(f.email.trim())}`} style={{ fontWeight: 700 }}>Sign in instead</a></>}</> : undefined} />
          <Input size="lg" label="Your name" autoComplete="name" value={f.name} onChange={(e) => edit("name", e.target.value)} error={problems.name} />
          <Input size="lg" label="Company" autoComplete="organization" value={f.company} onChange={(e) => edit("company", e.target.value)} error={problems.company} />
          <PasswordInput autoComplete="new-password" value={f.password} onChange={(e) => edit("password", e.target.value)} hint={passwordHint} error={problems.password} />
        </div>
        {problems.form && <p role="alert" style={{ ...authText, fontSize: 14, color: "var(--status-mismatch-fg)" }}>{problems.form}</p>}
        <AuthActions column>
          <Button type="submit" size="lg" fullWidth loading={busy}>Create account</Button>
          <p style={{ ...authNote, fontSize: 13 }}>By creating an account, you agree to the <a href={`${siteUrl}/terms`} target="_blank" rel="noreferrer">Terms of Service</a> and <a href={`${siteUrl}/privacy`} target="_blank" rel="noreferrer">Privacy Policy</a>.</p>
          <p style={authNote}>Already have an account? <a href="/" style={{ fontWeight: 700 }}>Sign in</a></p>
        </AuthActions>
      </form>
    </AuthFrame>
  );
}
